import React from 'react';
import { Shield, Eye, Database, Share2, UserCheck, Bell, MessageCircle } from 'lucide-react';
import { motion } from 'framer-motion';

const Section = ({ title, icon: Icon, children }: { title: string, icon: any, children: React.ReactNode }) => (
  <motion.section 
    initial={{ opacity: 0, y: 20 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true }}
    className="mb-12"
  >
    <div className="flex items-center gap-3 mb-6">
      <div className="w-10 h-10 rounded-xl bg-gold/10 flex items-center justify-center text-primary">
        <Icon className="w-5 h-5" />
      </div>
      <h2 className="text-2xl font-black text-foreground">{title}</h2>
    </div>
    <div className="bg-card rounded-3xl p-8 border border-border shadow-xl shadow-foreground/5 text-foreground/70 leading-relaxed font-medium text-justify">
      {children}
    </div>
  </motion.section>
);

export default function PrivacyPolicy() {
  return (
    <div className="pt-32 pb-24 bg-background min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-16 text-center">
          <span className="text-primary font-black uppercase tracking-[0.4em] text-xs mb-4 block">Privasi Anda Prioritas Kami</span>
          <h1 className="text-4xl md:text-6xl font-black text-foreground tracking-tighter mb-6 italic">
            Kebijakan <span className="text-primary">Privasi.</span>
          </h1>
          <p className="text-foreground/40 max-w-2xl mx-auto font-bold uppercase tracking-widest text-[10px]">
            Terakhir Diperbarui: 24 April 2024
          </p>
        </div>

        <div className="space-y-4">
          <Section title="Data yang Kami Kumpulkan" icon={Database}>
            <p className="mb-4">
              Azman204 mengumpulkan informasi untuk memberikan layanan yang lebih baik kepada semua pengguna kami. Informasi yang kami kumpulkan meliputi:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li><strong>Informasi Profil:</strong> Nama, alamat email, dan nomor telepon saat Anda mendaftar.</li>
              <li><strong>Informasi Transaksi:</strong> Data mengenai produk yang Anda beli dan riwayat pembayaran Anda.</li>
              <li><strong>Informasi Perangkat:</strong> Alamat IP, jenis browser, dan sistem operasi untuk tujuan keamanan dan peningkatan layanan.</li>
            </ul>
          </Section>

          <Section title="Bagaimana Kami Menggunakan Data" icon={Eye}>
            <p className="mb-4">
              Informasi yang kami kumpulkan digunakan untuk:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Menyediakan, memelihara, dan meningkatkan kualitas layanan kami.</li>
              <li>Memproses transaksi dan mengirimkan notifikasi terkait pembelian.</li>
              <li>Mengirimkan informasi pembaruan produk atau penawaran yang relevan bagi bisnis Anda.</li>
              <li>Mendeteksi, mencegah, dan menangani masalah keamanan atau teknis.</li>
            </ul>
          </Section>

          <Section title="Cookies dan Iklan Pihak Ketiga" icon={Shield}>
            <p className="mb-4">
              Azman204 menggunakan cookies untuk meningkatkan pengalaman pengguna. Selain itu:
            </p>
            <ul className="list-disc pl-5 space-y-2">
              <li>Vendor pihak ketiga, termasuk Google, menggunakan cookie untuk menayangkan iklan berdasarkan kunjungan sebelumnya ke situs web Anda atau situs web lain.</li>
              <li>Penggunaan cookie iklan oleh Google memungkinkan Google dan mitranya menayangkan iklan kepada pengguna Anda berdasarkan kunjungan mereka ke situs Anda dan/atau situs lain di Internet.</li>
              <li>Pengguna dapat menyisih dari iklan yang dipersonalisasi dengan mengunjungi <a href="https://www.google.com/settings/ads" target="_blank" rel="noopener" className="text-primary underline">Setelan Iklan</a>.</li>
            </ul>
          </Section>

          <Section title="Berbagi Informasi" icon={Share2}>
            <p>
              Kami <strong>tidak akan menjual, menyewakan, atau memberikan</strong> data pribadi Anda kepada pihak ketiga untuk tujuan pemasaran tanpa izin Anda. Kami hanya berbagi informasi dengan penyedia layanan pihak ketiga yang membantu operasional kami (seperti prosesor pembayaran) dan mereka terikat secara kontraktual untuk menjaga kerahasiaan data Anda.
            </p>
          </Section>

          <Section title="Kontrol Pengguna" icon={UserCheck}>
            <p>
              Anda memiliki hak untuk mengakses, memperbarui, atau menghapus informasi pribadi Anda kapan saja melalui dashboard akun Anda. Jika Anda mengalami kesulitan, tim layanan pelanggan kami siap membantu proses tersebut.
            </p>
          </Section>

          <Section title="Keamanan Informasi" icon={Shield}>
            <p>
              Kami menerapkan standar keamanan industri untuk melindungi informasi Anda dari akses, perubahan, pengungkapan, atau penghancuran yang tidak sah. Ini termasuk enkripsi data dan audit keamanan berkala pada sistem kami.
            </p>
          </Section>

          <Section title="Perubahan Kebijakan" icon={Bell}>
            <p>
              Kami dapat memperbarui Kebijakan Privasi ini dari waktu ke waktu. Setiap perubahan akan diumumkan di halaman ini dan jika perubahannya signifikan, kami akan mengirimkan notifikasi melalui email terdaftar Anda.
            </p>
          </Section>
        </div>

        {/* WhatsApp CTA */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="mt-24 p-8 md:p-12 rounded-[2.5rem] bg-green-500/5 relative overflow-hidden border border-green-500/10 flex flex-col items-center text-center"
        >
          {/* Watermark Logo */}
          <div className="absolute top-1/2 right-0 -translate-y-1/2 pointer-events-none opacity-[0.05]">
            <MessageCircle className="w-64 h-64 text-green-500" />
          </div>
          
          <div className="relative z-10">
            <div className="w-12 h-12 rounded-full bg-card shadow-sm flex items-center justify-center text-[#25d366] mb-6 mx-auto">
              <MessageCircle className="w-6 h-6 fill-current" />
            </div>
            
            <h3 className="text-2xl md:text-3xl font-black text-foreground mb-4">Butuh Respons Cepat?</h3>
            <p className="text-foreground/60 max-w-sm mx-auto font-medium mb-10 text-base md:text-lg">
              Hubungi admin kami langsung melalui pesan WhatsApp.
            </p>
            
            <a 
              href="https://wa.me/6281261089400" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#25d366] text-white px-8 py-4 md:px-10 md:py-5 rounded-2xl font-black text-lg hover:scale-105 active:scale-95 transition-all shadow-[0_10px_30px_rgba(37,211,102,0.2)]"
            >
              <MessageCircle className="w-6 h-6 fill-current" />
              Chat via WhatsApp
            </a>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
